import { useEffect, useMemo, useRef, useState } from 'react';
import AppHeader from './components/AppHeader.jsx';
import HeadwordDirectory from './components/HeadwordDirectory.jsx';
import PracticeWorkspace from './components/PracticeWorkspace.jsx';
import SubtopicDirectory from './components/SubtopicDirectory.jsx';
import ThemeDirectory from './components/ThemeDirectory.jsx';
import { generateRound } from './lib/api.js';
import { flattenHeadwords, listeningTargets, loadMaster, loadSubtopic, loadTheme } from './lib/data.js';
import { storedGuidedPassage } from './lib/guided.js';
import {
  getRecentTargets,
  loadProgress,
  recordBestScore,
  saveProgress,
  setRecentTargets,
} from './lib/progress.js';
import { scrollToTop } from './lib/utils.js';

function Loading({ message }) {
  return (
    <main className="page state-page" aria-live="polite" aria-busy="true">
      <span className="spinner" aria-hidden="true" />
      <p className="eyebrow">PROGRESSIVE ENGLISH VOCABULARY</p>
      <h1>{message}</h1>
    </main>
  );
}

function ErrorState({ message, onBack }) {
  return (
    <main className="page state-page" role="alert">
      <span className="state-icon">!</span>
      <p className="eyebrow coral-eyebrow">SOMETHING WENT WRONG</p>
      <h1>We could not open this part of the app.</h1>
      <p>{message}</p>
      <button className="primary-button" type="button" onClick={onBack}>Return to themes</button>
    </main>
  );
}

export default function App() {
  const [master, setMaster] = useState(null);
  const [progress, setProgress] = useState(loadProgress);
  const [screen, setScreen] = useState('loading');
  const [loadingMessage, setLoadingMessage] = useState('Loading the seven-theme database…');
  const [error, setError] = useState(null);
  const [themeBundle, setThemeBundle] = useState(null);
  const [subtopicBundle, setSubtopicBundle] = useState(null);
  const [headword, setHeadword] = useState(null);
  const [headwordLevels, setHeadwordLevels] = useState({});
  const [activeMode, setActiveMode] = useState('guided-practice');
  const [rounds, setRounds] = useState({});
  const [generatingMode, setGeneratingMode] = useState('');
  const [generationError, setGenerationError] = useState('');
  const guidedPassageRequest = useRef(0);

  useEffect(() => {
    const controller = new AbortController();
    loadMaster({ signal: controller.signal })
      .then((value) => {
        setMaster(value);
        setScreen('themes');
      })
      .catch((loadError) => {
        if (loadError.name !== 'AbortError') {
          setError(loadError.message);
          setScreen('error');
        }
      });
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (screen === 'workspace') scrollToTop();
  }, [screen]);

  const theme = themeBundle?.entry ?? null;
  const subtopic = subtopicBundle?.content?.subtopic ?? subtopicBundle?.route ?? null;
  const headwords = useMemo(
    () => subtopicBundle?.content ? flattenHeadwords(subtopicBundle.content) : [],
    [subtopicBundle],
  );

  function home() {
    guidedPassageRequest.current += 1;
    setThemeBundle(null);
    setSubtopicBundle(null);
    setHeadword(null);
    setRounds({});
    setError(null);
    setGenerationError('');
    setScreen('themes');
  }

  async function openTheme(themeId) {
    setLoadingMessage('Opening the selected theme…');
    setScreen('loading');
    try {
      const bundle = await loadTheme(themeId);
      setThemeBundle(bundle);
      setSubtopicBundle(null);
      setHeadword(null);
      setScreen('subtopics');
    } catch (loadError) {
      setError(loadError.message);
      setScreen('error');
    }
  }

  async function openSubtopic(subtopicId) {
    guidedPassageRequest.current += 1;
    setLoadingMessage('Loading the selected subtopic…');
    setScreen('loading');
    try {
      const bundle = await loadSubtopic(theme.id, subtopicId);
      setSubtopicBundle(bundle);
      setHeadword(null);
      setRounds({});
      setGenerationError('');
      if (theme.groupId === 'vocabulary-collocation') {
        setScreen('headwords');
      } else {
        setActiveMode('listen-spell');
        setScreen('workspace');
      }
    } catch (loadError) {
      setError(loadError.message);
      setScreen('error');
    }
  }

  function openHeadword(headwordId) {
    const selected = headwords.find((item) => item.id === headwordId);
    if (!selected) return;
    setHeadwordLevels((current) => ({
      ...current,
      [`${theme.id}::${subtopic.id}`]: selected.level,
    }));
    activateHeadword(selected);
  }

  function activateHeadword(selected) {
    const storedPassage = storedGuidedPassage(selected);
    const requestId = guidedPassageRequest.current + 1;
    guidedPassageRequest.current = requestId;
    setHeadword(selected);
    setActiveMode('guided-practice');
    setRounds({
      'guided-practice': {
        type: 'stored',
        items: selected.collocations,
        key: `stored-${selected.id}`,
        headwordId: selected.id,
        passage: storedPassage,
        passageStatus: storedPassage ? 'ready' : 'loading',
        passageSource: storedPassage ? 'stored' : undefined,
      },
    });
    setGenerationError('');
    setScreen('workspace');
    if (!storedPassage) void prepareGuidedPassage(selected, requestId);
  }

  async function openSearchedHeadword(result) {
    guidedPassageRequest.current += 1;
    setLoadingMessage('Opening the selected headword…');
    setScreen('loading');
    try {
      const bundle = await loadSubtopic(result.themeId, result.subtopicId);
      const selected = flattenHeadwords(bundle.content)
        .find((item) => item.id === result.headwordId);
      if (!selected) throw new Error(`Unknown headword: ${result.headwordId}`);
      setThemeBundle(bundle);
      setSubtopicBundle(bundle);
      setHeadwordLevels((current) => ({
        ...current,
        [`${result.themeId}::${result.subtopicId}`]: selected.level,
      }));
      setError(null);
      setGenerationError('');
      activateHeadword(selected);
    } catch (loadError) {
      setError(loadError.message);
      setScreen('error');
    }
  }

  function identity(modeId) {
    return {
      themeId: theme.id,
      subtopicId: subtopic.id,
      headwordId: headword?.id,
      mode: modeId,
    };
  }

  function completeRound(modeId, roundResult) {
    const roundType = rounds[modeId]?.type ?? 'stored';
    let next = recordBestScore(progress, identity(modeId), { ...roundResult, roundType });
    next = setRecentTargets(next, identity(modeId), roundResult.targetIds ?? []);
    next = saveProgress(next);
    setProgress(next);
  }

  function vocabularyPayload(modeId, selectedHeadword = headword) {
    const payload = {
      mode: modeId,
      theme: {
        id: theme.id,
        title: theme.title,
        title_zh: theme.titleZh ?? themeBundle.index.theme.titleZh,
      },
      subtopic: {
        id: subtopic.id,
        title: subtopic.title,
        title_zh: subtopic.titleZh,
      },
      headword: {
        id: selectedHeadword.id,
        text: selectedHeadword.displayText,
        part_of_speech: selectedHeadword.partOfSpeech,
        definition_zh: selectedHeadword.definitionZh,
      },
      previous_targets: getRecentTargets(progress, {
        themeId: theme.id,
        subtopicId: subtopic.id,
        headwordId: selectedHeadword.id,
        mode: modeId,
      }),
    };
    payload.fixed_collocations = selectedHeadword.collocations.map((item) => {
      const choice = item.guidedPractice.collocationChoice;
      const correctOption = choice.options.find((option) => option.id === choice.correctOptionId);
      return {
        id: item.id,
        canonical_form: item.canonicalForm,
        accepted_variants: item.acceptedCanonicalVariants ?? [item.canonicalForm],
        gloss_zh: item.glossZh,
        avoid_sentences: [
          choice.sentenceWithBlank.replace('{{blank}}', correctOption?.text ?? item.canonicalForm),
          item.guidedPractice.sentenceUpgrade?.sentence,
          item.guidedPractice.sentenceUpgrade?.modelAnswer?.text,
          item.exampleSentence?.text,
          item.sentenceBuilding?.modelAnswer?.text,
        ].filter(Boolean),
      };
    });
    return payload;
  }

  async function prepareGuidedPassage(selectedHeadword = headword, existingRequestId = null) {
    if (!selectedHeadword) return;
    if (selectedHeadword.guidedPracticePassage) {
      setRounds((current) => ({
        ...current,
        'guided-practice': {
          ...(current['guided-practice'] ?? {
            type: 'stored',
            items: selectedHeadword.collocations,
            key: `stored-${selectedHeadword.id}`,
          }),
          headwordId: selectedHeadword.id,
          passage: selectedHeadword.guidedPracticePassage,
          passageStatus: 'ready',
          passageSource: 'stored',
          passageError: '',
        },
      }));
      return;
    }
    const requestId = existingRequestId ?? guidedPassageRequest.current + 1;
    guidedPassageRequest.current = requestId;
    setRounds((current) => ({
      ...current,
      'guided-practice': {
        ...(current['guided-practice'] ?? {
          type: 'stored',
          items: selectedHeadword.collocations,
          key: `stored-${selectedHeadword.id}`,
        }),
        headwordId: selectedHeadword.id,
        passageStatus: 'loading',
        passageError: '',
      },
    }));
    try {
      const generated = await generateRound({
        ...vocabularyPayload('guided-practice', selectedHeadword),
        guided_scope: 'passage-only',
      });
      if (!generated.passage) throw new Error('The generated round did not contain the context passage.');
      if (guidedPassageRequest.current !== requestId) return;
      setRounds((current) => {
        const storedRound = current['guided-practice'];
        if (storedRound?.headwordId !== selectedHeadword.id || storedRound.type !== 'stored') return current;
        return {
          ...current,
          'guided-practice': {
            ...storedRound,
            passage: generated.passage,
            passageStatus: 'ready',
            passageSource: generated.status === 'cached' ? 'cached' : 'ai',
          },
        };
      });
    } catch (passageGenerationError) {
      if (guidedPassageRequest.current !== requestId) return;
      setRounds((current) => {
        const storedRound = current['guided-practice'];
        if (storedRound?.headwordId !== selectedHeadword.id || storedRound.type !== 'stored') return current;
        return {
          ...current,
          'guided-practice': {
            ...storedRound,
            passageStatus: 'error',
            passageError: passageGenerationError.message,
          },
        };
      });
    }
  }

  async function startAiRound(modeId) {
    if (modeId === 'guided-practice') guidedPassageRequest.current += 1;
    setGeneratingMode(modeId);
    setGenerationError('');
    try {
      let payload = {
        mode: modeId,
        theme: {
          id: theme.id,
          title: theme.title,
          title_zh: theme.titleZh ?? themeBundle.index.theme.titleZh,
        },
        subtopic: {
          id: subtopic.id,
          title: subtopic.title,
          title_zh: subtopic.titleZh,
        },
        previous_targets: getRecentTargets(progress, identity(modeId)),
      };

      if (headword) {
        payload = vocabularyPayload(modeId, headword);
        if (modeId === 'guided-practice') payload.guided_scope = 'full';
      } else {
        payload.targets = listeningTargets(subtopicBundle.content, subtopicBundle.catalog).map((target) => ({
          id: target.id,
          text: target.displayText,
          text_zh: target.textZh,
          accepted_spellings: target.acceptedSpellings,
          speech_text: target.audio?.speechText ?? target.displayText,
        }));
      }

      const generated = await generateRound(payload);
      const expected = modeId === 'listen-spell' ? 10 : 5;
      if (!Array.isArray(generated.items) || generated.items.length !== expected) {
        throw new Error('The generated round did not contain the required number of items.');
      }
      if (modeId === 'guided-practice' && !generated.passage) {
        throw new Error('The generated round did not contain the context passage.');
      }
      setRounds((current) => ({
        ...current,
        [modeId]: {
          type: generated.status === 'cached' ? 'cached' : 'ai',
          items: generated.items,
          passage: generated.passage,
          passageStatus: modeId === 'guided-practice' ? 'ready' : undefined,
          headwordId: headword?.id,
          key: `ai-${Date.now()}`,
        },
      }));
      setActiveMode(modeId);
    } catch (generationErrorValue) {
      setGenerationError(generationErrorValue.message);
    } finally {
      setGeneratingMode('');
    }
  }

  return (
    <div className="app-shell">
      <AppHeader app={master?.app} progress={progress} onHome={home} onProgressReset={setProgress} />

      {screen === 'loading' && <Loading message={loadingMessage} />}
      {screen === 'error' && <ErrorState message={error} onBack={home} />}
      {screen === 'themes' && master && (
        <ThemeDirectory
          master={master}
          progress={progress}
          onOpen={openTheme}
          onOpenHeadword={openSearchedHeadword}
        />
      )}
      {screen === 'subtopics' && themeBundle && (
        <SubtopicDirectory theme={theme} index={themeBundle.index} progress={progress} onOpen={openSubtopic} onBack={home} />
      )}
      {screen === 'headwords' && subtopicBundle && (
        <HeadwordDirectory
          theme={theme}
          subtopic={subtopic}
          headwords={headwords}
          progress={progress}
          level={headwordLevels[`${theme.id}::${subtopic.id}`] ?? 'all'}
          onLevelChange={(level) => setHeadwordLevels((current) => ({
            ...current,
            [`${theme.id}::${subtopic.id}`]: level,
          }))}
          onOpen={openHeadword}
          onBack={() => setScreen('subtopics')}
        />
      )}
      {screen === 'workspace' && subtopicBundle && (
        <PracticeWorkspace
          theme={theme}
          subtopic={subtopic}
          headword={headword}
          content={subtopicBundle.content}
          progress={progress}
          activeMode={activeMode}
          onModeChange={(mode) => {
            setActiveMode(mode);
            setGenerationError('');
          }}
          rounds={rounds}
          generatingMode={generatingMode}
          generationError={generationError}
          onGenerate={startAiRound}
          onPrepareGuidedPassage={() => prepareGuidedPassage(headword)}
          onComplete={completeRound}
          onExit={() => setScreen(headword ? 'headwords' : 'subtopics')}
        />
      )}
    </div>
  );
}
